import React, { useEffect, useState, useMemo, useCallback, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  CssBaseline,
  Backdrop,
  CircularProgress,
  useTheme,
  useMediaQuery,
  Dialog,
  DialogTitle,
  DialogContent,
  TextField,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Switch,
  FormControlLabel,
  Autocomplete,
  Snackbar,
  Alert,
  FormHelperText,
  Chip,
  Tooltip,
  ToggleButton,
  ToggleButtonGroup,
} from "@mui/material";
import Groups2Icon from '@mui/icons-material/Groups2';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import TableRowsIcon from '@mui/icons-material/TableRows';
import VisibilityIcon from '@mui/icons-material/Visibility';
import { Add, Cancel, Delete, Edit, Padding, Remove, Save, Menu, FilterList } from "@mui/icons-material";
import { DataSets as DataSetStructure } from "modules/Interfaces/InstanUpdates/DataSets";
import { Logger } from "utils/Logger";
import { trackAction } from "utils/Infralytiqs";
import { useSelector } from "react-redux";
import { RootState } from "state/store";
import { ThemeProvider } from "@emotion/react";
import { alpha } from "@mui/material/styles";
import { useNavigate } from "react-router-dom";
import { DynamicBreadcrumbs } from "components/Breadcrumb";
import { CamelCaseToName } from "utils/CamelCaseToName";
import { apiClient } from "utils/ApiClient";
import { useModulePermissions } from 'hooks/useModulePermissions';
import { DataTableWithStaticSchema } from 'components/DataTable';
import { GET_COLLECTION_DATASETS_URL } from 'envConfig';
import {
  SWIPE_ACTION_WIDTH_PX,
  SWIPE_DIRECTION_LOCK_PX,
  clampSwipeOffset,
  computeSnappedOffset,
} from 'utils/iosSwipeRow';

export const DataSets: React.FC = () => {

  const theme = useTheme();
  const heroTileFg = theme.palette.dashboard?.heroTileForegroundColor;
  const heroTileLabelColor = theme.palette.dashboard?.heroTileLabelColor
    ?? (heroTileFg ? 'rgba(0, 0, 0, 0.65)' : 'rgba(255,255,255,0.7)');
  const heroTileTextShadow = heroTileFg ? 'none' : '0 2px 8px rgba(0,0,0,0.3)';
  const reservedDatasetTileGradient = theme.palette.dashboard?.reservedDatasetTileGradient
    ?? 'linear-gradient(135deg, #1a237e 0%, #283593 50%, #3949ab 100%)';
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const user = useSelector((state: RootState) => state.authentication.user);

  /** Mobile/tablet: iOS-style swipe on dataset tiles */
  const [datasetTileSwipeRow, setDatasetTileSwipeRow] = useState<{
    tileId: string;
    offset: number;
    dragging: boolean;
  } | null>(null);
  const datasetSwipeSessionRef = useRef<{
    tileId: string;
    startX: number;
    startY: number;
    baseOffset: number;
    lock: 'h' | 'v' | null;
  } | null>(null);
  const datasetLatestSwipeOffsetRef = useRef(0);
  const { canManageDatasets, canViewOnly, isUserSuperAdmin } = useModulePermissions();
  const [dataSets, setDataSets] = useState<any[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false); // For Add New DataSet / Edit Schema
  const [statusDialogOpen, setStatusDialogOpen] = useState<boolean>(false); // For Edit Status
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [currentDataSet, setCurrentDataSet] = useState<any>(null);
  const [currentStatusDataSet, setCurrentStatusDataSet] = useState<any>(null); // For status edit
  const [collectionNameError, setCollectionNameError] = useState<string>(''); // State for collectionName validation

  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' as 'success' | 'error' }); // Snackbar state
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false); // Mobile menu dialog state
  const [toolbarMenuOpen, setToolbarMenuOpen] = useState(false);

  // View mode toggle state: 'tile' for card view, 'table' for table view
  // Persisted in localStorage so the preference survives navigation
  const VIEWMODE_STORAGE_KEY = 'datasets-view-mode';
  const [viewMode, setViewMode] = useState<'tile' | 'table'>(() => {
    const stored = localStorage.getItem(VIEWMODE_STORAGE_KEY);
    return stored === 'table' ? 'table' : 'tile';
  });
  const [tableRefreshTrigger, setTableRefreshTrigger] = useState<number>(0);

  const navigate = useNavigate();

  // New state for search and sort
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [sortField, setSortField] = useState<string>("collectionUpdatedOn"); // 'collectionName' or 'collectionUpdatedOn'
  const [sortOrder, setSortOrder] = useState<string>("desc"); // 'asc' or 'desc'
  const [statusFilter, setStatusFilter] = useState<string>("active"); // 'active', 'inactive', 'deleted', or 'all'

  const nameRegex = new RegExp("^[a-zA-Z0-9_\\-]*$"); // Regex for alphabets, numbers, underscore, and hyphen - FIXED: used RegExp constructor

  // Schema for the DataSets table view (used by DataTableWithStaticSchema)
  const dataSetsSchema = useMemo(() => ({
    paths: {
      _id: { instance: 'String', isRequired: false },
      dataSetName: { instance: 'String', isRequired: false },
      collectionStatus: { instance: 'String', isRequired: false },
      accessLevel: { instance: 'String', isRequired: false },
      contactsCount: { instance: 'Number', isRequired: false },
      collectionOwnedByName: { instance: 'String', isRequired: false },
      collectionUpdatedByName: { instance: 'String', isRequired: false },
      createdAt: { instance: 'Date', isRequired: false },
      collectionUpdatedOn: { instance: 'Date', isRequired: false },
    },
    obj: {}
  }), []);

  const DATASETS_MANDATORY_COLUMNS = useMemo(() => ['dataSetName', 'collectionStatus', 'accessLevel', 'contactsCount'], []);
  const DATASETS_OPTIONAL_COLUMNS = useMemo(() => ['collectionOwnedByName', 'collectionUpdatedByName', 'createdAt', 'collectionUpdatedOn'], []);
  const DATASETS_DISABLED_FILTER_COLUMNS = useMemo(() => ['contactsCount', 'createdAt', 'collectionUpdatedOn'], []);

  // Custom data processor to transform API data for the table view
  const enrichDataSetData = useCallback((data: any[]) => {
    return data.map((ds: any) => ({
      ...ds,
      dataSetName: ds.collectionName?.split("-").slice(2).join("-") || ds.collectionName,
      collectionOwnedByName: ds.collectionName?.split("-").slice(2).join("-") === "Consent_Records"
        ? 'System' : (ds.collectionOwnedByName || 'Unknown User'),
      collectionUpdatedByName: ds.collectionName?.split("-").slice(2).join("-") === "Consent_Records"
        ? 'System' : (ds.collectionUpdatedByName || ds.collectionOwnedByName || 'Unknown User'),
    }));
  }, []);

  // Build data URL for the table view based on status filter
  const tableDataUrl = useMemo(() => {
    let endpoint = '/iu/ds/cmp/set';
    if (statusFilter !== 'active') {
      endpoint += `?status=${statusFilter}`;
    }
    return endpoint;
  }, [statusFilter]);

  // Type definition for validation errors
  type ValidationError = {
    index: number;
    errors: string[];
  };

  const showMessage = (message: string, severity: 'success' | 'error') => {
    setSnackbar({ open: true, message, severity });
  };

  const fetchDataSets = async () => {
    setLoading(true);
    try {
      // Build endpoint path with status filter query parameter
      let endpoint = '/iu/ds/cmp/set';
      if (statusFilter !== 'active') { // Only add query param if not default
        endpoint += `?status=${statusFilter}`;
      }
      
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      const data = await apiClient.get(endpoint);
      setDataSets(Array.isArray(data.data) ? data.data.map((ds: any) => ({
        ...ds,
        collectionUpdatedOn: new Date(ds.collectionUpdatedOn),
      })) : []);
      showMessage('Data sets loaded successfully!', 'success');
    } catch (error) {
      Logger.error("Failed to fetch dataSets:", error);
      showMessage(`Failed to fetch data sets: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDataSets();
  }, [statusFilter]);

  const saveDataSet = async () => {
    if (!currentDataSet) return;

    if (collectionNameError) {
      return;
    }

    // Prevent creation of "Consent_Records" dataset manually
    if (!isEditing && currentDataSet.collectionName === "Consent_Records") {
      showMessage('Cannot create dataset with name "Consent_Records". This is a reserved dataset name.', 'error');
      return;
    }

    // Validate schema fields
    const schemaValidationErrors: ValidationError[] = currentDataSet.collectionSchema.map((field: any, index: number) => {
      const errors: string[] = [];
      if (!field.keyName || field.keyName.trim() === '') {
        errors.push('Field name is required');
      }
      if (!field.type || field.type.trim() === '') {
        errors.push('Field type is required');
      }
      return { index, errors };
    }).filter((item: ValidationError) => item.errors.length > 0);

    if (schemaValidationErrors.length > 0) {
      // Update the schema with error messages
      const updatedSchema = currentDataSet.collectionSchema.map((field: any, index: number) => {
        const validationError = schemaValidationErrors.find((err: ValidationError) => err.index === index);
        if (validationError) {
          return { ...field, error: validationError.errors.join(', ') };
        }
        return field;
      });
      
      setCurrentDataSet((prev: any) => ({
        ...prev,
        collectionSchema: updatedSchema
      }));
      
      showMessage('Please fill in all required fields for schema', 'error');
      return;
    }

    const schemaHasErrors = currentDataSet.collectionSchema.some((field: any) => field.error);
    if (schemaHasErrors) {
      return;
    }

    setLoading(true);
    try {
      const payload = {
        collectionName: currentDataSet.collectionName,
        accessLevel: currentDataSet.accessLevel,
        schema: currentDataSet.collectionSchema
      };
      Logger.info("Payload", JSON.stringify(payload));
      
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      if (isEditing) {
        await apiClient.patch('/iu/ds/cmp/set', payload);
      } else {
        await apiClient.put('/iu/ds/cmp/set', payload);
      }

      trackAction(
        isEditing ? 'dataset_updated' : 'dataset_created',
        undefined,
        {
          collection_name: payload.collectionName,
          access_level: payload.accessLevel,
          field_count: Array.isArray(payload.schema) ? payload.schema.length : 0,
          user_email: user?.email,
        },
      );

      await fetchDataSets();
      setTableRefreshTrigger(prev => prev + 1);
      showMessage('Data set saved successfully!', 'success');
      closeDialog();
    } catch (error) {
      Logger.error("Failed to save  dataSet:", error);
      showMessage(`Failed to save data set: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveStatus = async () => {
    if (!currentStatusDataSet || !currentStatusDataSet.collectionName || (!currentStatusDataSet.collectionStatus && !currentStatusDataSet.accessLevel)) return;

    setLoading(true);
    try {
      const payload: any = {
        collectionName: currentStatusDataSet.collectionName?.split("-").slice(2).join("-")
      };
      
      // Add status if provided
      if (currentStatusDataSet.collectionStatus) {
        payload.status = currentStatusDataSet.collectionStatus;
      }
      
      // Add access level if provided
      if (currentStatusDataSet.accessLevel) {
        payload.accessLevel = currentStatusDataSet.accessLevel;
      }
      
      // Use ApiClient instead of fetch (automatically includes Authorization header)
      await apiClient.patch('/iu/ds/cmp/set', payload);
      await fetchDataSets();
      setTableRefreshTrigger(prev => prev + 1);
      showMessage('Collection updated successfully!', 'success');
      closeStatusDialog();
    } catch (error) {
      Logger.error("Failed to update collection:", error);
      showMessage(`Failed to update collection: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteDataSet = async (collectionName: string, collectionStatus?: string) => {
    setDatasetTileSwipeRow(null);
    setLoading(true);
    try {
      const encoded = encodeURIComponent(collectionName);
      const isPermanent = collectionStatus === 'deleted';
      // Soft-delete: mark deleted. Already deleted: drop Mongo collection + remove tracker row
      await apiClient.delete(
        isPermanent ? `/iu/ds/cmp/set/${encoded}/permanent` : `/iu/ds/cmp/set/${encoded}`
      );

      await fetchDataSets();
      setTableRefreshTrigger(prev => prev + 1);
      showMessage(
        isPermanent ? 'Data set permanently removed.' : 'Data set deleted successfully!',
        'success'
      );
    } catch (error) {
      Logger.error("pg-qa-ported-fe/src/modules/InstantUpdate/DataSets/DataSets.tsx : handleDeleteDataSet(): ", "Failed to delete dataSet:" + error);
      showMessage(`Failed to delete data set: ${error instanceof Error ? error.message : String(error)}`, 'error');
    } finally {
      setLoading(false);
    }
  };

  const closeDialog = () => {
    setDialogOpen(false);
    setCurrentDataSet(null);
    setCollectionNameError('');
  };

  const closeStatusDialog = () => {
    setStatusDialogOpen(false);
    setCurrentStatusDataSet(null);
  };

  const handleFieldChange = (index: number, field: string, value: string | boolean) => {
    if (field === 'keyName' && typeof value === 'string') {
      const regex = new RegExp("^[a-zA-Z0-9_\\-]*$"); // FIXED: used RegExp constructor
      if (!regex.test(value)) {
        setCurrentDataSet((prev: any) => ({
          ...prev,
          collectionSchema: prev?.collectionSchema?.map((item: any, i: number) => (i === index ? { ...item, keyName: value, error: 'Only alphabets, numbers, underscore and hyphen are allowed.' } : item)),
        }));
        return;
      } else {
        setCurrentDataSet((prev: any) => ({
          ...prev,
          collectionSchema: prev?.collectionSchema?.map((item: any, i: number) => (i === index ? { ...item, keyName: value, error: '' } : item)),
        }));
        return;
      }
    }
    setCurrentDataSet((prev: any) => ({
      ...prev,
      collectionSchema: prev?.collectionSchema?.map((item: any, i: number) => (i === index ? { ...item, [field]: value } : item)),
    }));
  };

  const handleAddFieldSet = () => {
    setCurrentDataSet((prev: any) => ({
      ...prev,
      collectionSchema: [...(prev?.collectionSchema || []), { keyName: "", type: "String", required: false, error: '' }],
    }));
  };

  const handleRemoveFieldSet = (index: number) => {
    setCurrentDataSet((prev: any) => ({
      ...prev,
      collectionSchema: prev?.collectionSchema?.filter((_: any, i: number) => i !== index),
    }));
  };

  const openNewDataSetDialog = (dataSet?: any) => {
    setDatasetTileSwipeRow(null);
    setCurrentDataSet(
      dataSet ? { ...dataSet, collectionUpdatedOn: new Date(dataSet.collectionUpdatedOn) }
        : {
          collectionName: "",
          companyId: "",
          collectionStatus: "",
          accessLevel: "Public",
          sharedTo: [],
          collectionOwnedByUserId: "",
          collectionUpdatedByUserId: "",
          collectionUpdatedOn: new Date(),
          collectionSchema: []
        }
    );
    setCollectionNameError('');
    setIsEditing(!!dataSet);
    setDialogOpen(true);
  };

  const openStatusDialog = (dataSet: any) => {
    setDatasetTileSwipeRow(null);
    setCurrentStatusDataSet(dataSet);
    setStatusDialogOpen(true);
  };


  const filteredAndSortedDataSets = useMemo(() => {
    let filtered = dataSets.filter((dataSet) => {
      const displayedName = dataSet.collectionName?.split("-").slice(2).join("-")?.toLowerCase() || "";
      const matchesSearch = displayedName.includes(searchTerm.toLowerCase());
      
      // Apply status filter
      const matchesStatusFilter = statusFilter === "all" || dataSet.collectionStatus === statusFilter;
      
      return matchesSearch && matchesStatusFilter;
    });

    filtered.sort((a, b) => {
      let comparison = 0;
      if (sortField === "collectionName") {
        const nameA = a.collectionName?.split("-").slice(2).join("-")?.toLowerCase() || "";
        const nameB = b.collectionName?.split("-").slice(2).join("-")?.toLowerCase() || "";
        if (nameA < nameB) comparison = -1;
        if (nameA > nameB) comparison = 1;
      } else if (sortField === "collectionUpdatedOn") {
        const dateA = a.collectionUpdatedOn instanceof Date ? a.collectionUpdatedOn.getTime() : new Date(a.collectionUpdatedOn).getTime();
        const dateB = b.collectionUpdatedOn instanceof Date ? b.collectionUpdatedOn.getTime() : new Date(b.collectionUpdatedOn).getTime();
        comparison = dateA - dateB;
      }

      return sortOrder === "asc" ? comparison : -comparison;
    });

    return filtered;
  }, [dataSets, searchTerm, sortField, sortOrder, statusFilter]);

  const handleDataSetTileTouchStart = useCallback(
    (e: React.TouchEvent, dataSet: any) => {
      if (!isMobile) return;
      const t = e.touches[0];
      setDatasetTileSwipeRow((prev) => {
        const anotherWasOpen = Boolean(
          prev && prev.tileId !== dataSet._id && Math.abs(prev.offset) > 0.5
        );
        const baseOffset =
          !anotherWasOpen && prev?.tileId === dataSet._id ? prev!.offset : 0;
        datasetSwipeSessionRef.current = {
          tileId: dataSet._id,
          startX: t.clientX,
          startY: t.clientY,
          baseOffset,
          lock: null,
        };
        datasetLatestSwipeOffsetRef.current = baseOffset;
        return { tileId: dataSet._id, offset: baseOffset, dragging: true };
      });
    },
    [isMobile]
  );

  const handleDataSetTileTouchMove = useCallback(
    (e: React.TouchEvent, dataSet: any, rowCanEdit: boolean, rowCanDelete: boolean) => {
      if (!isMobile) return;
      const sess = datasetSwipeSessionRef.current;
      if (!sess || sess.tileId !== dataSet._id) return;
      const touch = e.touches[0];
      const dx = touch.clientX - sess.startX;
      const dy = touch.clientY - sess.startY;

      if (sess.lock === null) {
        if (
          Math.abs(dx) > SWIPE_DIRECTION_LOCK_PX &&
          Math.abs(dx) > Math.abs(dy) * 1.2
        ) {
          sess.lock = 'h';
        } else if (
          Math.abs(dy) > SWIPE_DIRECTION_LOCK_PX &&
          Math.abs(dy) > Math.abs(dx) * 1.2
        ) {
          sess.lock = 'v';
        }
      }
      if (sess.lock !== 'h') return;

      const raw = sess.baseOffset + dx;
      const clamped = clampSwipeOffset(
        raw,
        rowCanEdit,
        rowCanDelete,
        SWIPE_ACTION_WIDTH_PX
      );
      datasetLatestSwipeOffsetRef.current = clamped;
      setDatasetTileSwipeRow({
        tileId: dataSet._id,
        offset: clamped,
        dragging: true,
      });
    },
    [isMobile]
  );

  const handleDataSetTileTouchEnd = useCallback(
    (
      e: React.TouchEvent,
      dataSet: any,
      rowCanEdit: boolean,
      rowCanDelete: boolean,
      datasetName: string
    ) => {
      if (!isMobile) return;
      const sess = datasetSwipeSessionRef.current;
      if (!sess || sess.tileId !== dataSet._id) {
        datasetSwipeSessionRef.current = null;
        return;
      }
      const t = e.changedTouches[0];
      const dx = t.clientX - sess.startX;
      const dy = t.clientY - sess.startY;
      const { lock, baseOffset } = sess;
      datasetSwipeSessionRef.current = null;

      const finishHorizontalSnap = (off: number) => {
        const snapped = computeSnappedOffset(
          off,
          rowCanEdit,
          rowCanDelete,
          SWIPE_ACTION_WIDTH_PX
        );
        datasetLatestSwipeOffsetRef.current = snapped;
        setDatasetTileSwipeRow({
          tileId: dataSet._id,
          offset: snapped,
          dragging: false,
        });
      };

      if (
        lock === null &&
        Math.abs(dx) > Math.abs(dy) * 1.2 &&
        Math.abs(dx) > 18
      ) {
        const raw = baseOffset + dx;
        const clamped = clampSwipeOffset(
          raw,
          rowCanEdit,
          rowCanDelete,
          SWIPE_ACTION_WIDTH_PX
        );
        finishHorizontalSnap(clamped);
        return;
      }

      if (lock !== 'h') {
        if (Math.abs(dx) < 12 && Math.abs(dy) < 12) {
          if (Math.abs(baseOffset) > 0.5) {
            datasetLatestSwipeOffsetRef.current = 0;
            setDatasetTileSwipeRow({
              tileId: dataSet._id,
              offset: 0,
              dragging: false,
            });
          } else {
            navigate(`/instant-updates/data-sets/${datasetName}`);
          }
        } else {
          datasetLatestSwipeOffsetRef.current = baseOffset;
          setDatasetTileSwipeRow({
            tileId: dataSet._id,
            offset: baseOffset,
            dragging: false,
          });
        }
        return;
      }

      finishHorizontalSnap(datasetLatestSwipeOffsetRef.current);
    },
    [isMobile, navigate]
  );

  const handleDataSetTileTouchCancel = useCallback(
    (
      _e: React.TouchEvent,
      dataSet: any,
      rowCanEdit: boolean,
      rowCanDelete: boolean
    ) => {
      if (!isMobile) return;
      const sess = datasetSwipeSessionRef.current;
      if (!sess || sess.tileId !== dataSet._id) return;
      datasetSwipeSessionRef.current = null;
      const snapped = computeSnappedOffset(
        datasetLatestSwipeOffsetRef.current,
        rowCanEdit,
        rowCanDelete,
        SWIPE_ACTION_WIDTH_PX
      );
      datasetLatestSwipeOffsetRef.current = snapped;
      setDatasetTileSwipeRow({
        tileId: dataSet._id,
        offset: snapped,
        dragging: false,
      });
    },
    [isMobile]
  );

  return (
    <Box pl={{ xs: 1.25, md: 2.5 }} pt={1} width="100%" mb={3}>
      {loading && (
        <Backdrop
          sx={(theme: any) => ({ color: theme.palette.secondary.main, zIndex: theme.zIndex.drawer + 1 })}
          open={loading}
        >
          <CircularProgress color="inherit" />
        </Backdrop>
      )}
      <DynamicBreadcrumbs items={[
        { itemPath: "/dashboard", itemLabel: "Home" },
        { itemPath: "/instant-updates/data-sets", itemLabel: "Data Sets" }
      ]} />
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={4}>
        <Typography variant="h4" gutterBottom>
          List of Data Sets
        </Typography>
      </Box>

      {/* Mobile / tablet: prominent NEW DATA SET above toolbar (not in burger menu) */}
      {(canManageDatasets() || isUserSuperAdmin) && (
        <Box
          sx={{
            display: { xs: 'flex', md: 'none' },
            justifyContent: 'center',
            mb: 2,
            px: { xs: 0.5, sm: 1 },
          }}
        >
          <Button
            variant="outlined"
            color="secondary"
            startIcon={<Add />}
            onClick={() => openNewDataSetDialog()}
            fullWidth
            sx={(theme) => ({
              maxWidth: 480,
              py: 1.25,
              fontWeight: 700,
              letterSpacing: '0.04em',
              borderWidth: 2,
              borderColor: 'secondary.main',
              color: 'secondary.main',
              textTransform: 'uppercase',
              '&:hover': {
                borderWidth: 2,
                borderColor: 'secondary.dark',
              },
              '@keyframes datasetsNewDataSetGlow': {
                '0%, 100%': {
                  boxShadow: `0 0 6px ${alpha(theme.palette.secondary.main, 0.55)}, 0 0 14px ${alpha(theme.palette.secondary.main, 0.35)}`,
                },
                '50%': {
                  boxShadow: `0 0 16px ${alpha(theme.palette.secondary.main, 0.95)}, 0 0 28px ${alpha(theme.palette.secondary.main, 0.5)}`,
                },
              },
              animation: 'datasetsNewDataSetGlow 2s ease-in-out infinite',
            })}
          >
            New Data Set
          </Button>
        </Box>
      )}

      {/* Responsive Toolbar Container */}
      <Box 
        sx={{
          display: 'flex',
          flexDirection: 'row',
          gap: { xs: 1, md: 2 },
          alignItems: 'center',
          mb: 3,
          p: 1.5,
          borderRadius: 2,
          backgroundColor: (theme) => theme.palette.mode === 'dark' ? 'rgba(0,0,0,0.45)' : 'rgba(0,0,0,0.06)',
        }}
      >
        {/* Mobile: Filter Icon Button - Left side */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center' }}>
          <IconButton
            color="secondary"
            onClick={() => setMobileMenuOpen(true)}
            size="large"
            sx={{
              border: '1px solid',
              borderColor: 'secondary.main',
              '&:hover': {
                backgroundColor: 'action.hover',
                borderColor: 'secondary.dark',
              },
            }}
          >
            <FilterList />
          </IconButton>
        </Box>

        {/* Mobile: Menu Icon Button for toolbar actions */}
        <Box sx={{ display: { xs: 'flex', md: 'none' }, alignItems: 'center', ml: 'auto' }}>
          <IconButton
            color="secondary"
            onClick={() => setToolbarMenuOpen(true)}
            size="large"
            sx={{
              border: '1px solid',
              borderColor: 'secondary.main',
              '&:hover': {
                backgroundColor: 'action.hover',
                borderColor: 'secondary.dark',
              },
            }}
          >
            <Menu />
          </IconButton>
        </Box>

        {/* Desktop: Search Box */}
        <Box sx={{ display: { xs: 'none', md: 'block' }, flexGrow: 1 }}>
          <TextField
            label="Search Data Sets"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ 
              flexGrow: 1,
              minWidth: '200px'
            }}
          />
        </Box>

        {/* Desktop: Sort Controls */}
        <Box 
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'row',
            gap: 1,
            flexWrap: 'wrap'
          }}
        >
          <FormControl sx={{ minWidth: 150 }} size="small">
            <InputLabel>Sort By</InputLabel>
            <Select
              value={sortField}
              label="Sort By"
              onChange={(e) => setSortField(e.target.value as string)}
            >
              <MenuItem value="collectionName">Collection Name</MenuItem>
              <MenuItem value="collectionUpdatedOn">Last Updated On</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 100 }} size="small">
            <InputLabel>Order</InputLabel>
            <Select
              value={sortOrder}
              label="Order"
              onChange={(e) => setSortOrder(e.target.value as string)}
            >
              <MenuItem value="asc">ASC</MenuItem>
              <MenuItem value="desc">DSC</MenuItem>
            </Select>
          </FormControl>
          
          <FormControl sx={{ minWidth: 120 }} size="small">
            <InputLabel>Status Filter</InputLabel>
            <Select
              value={statusFilter}
              label="Status Filter"
              onChange={(e) => setStatusFilter(e.target.value as string)}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="deleted">Deleted</MenuItem>
              <MenuItem value="all">All Status</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Action Buttons - Hidden on mobile, shown in toolbar dialog instead */}
        <Box 
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'row',
            gap: 1,
            alignItems: 'center',
            marginLeft: 0
          }}
        >
          {/* View Mode Toggle */}
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_e, newMode) => {
              if (newMode !== null) {
                setViewMode(newMode);
                localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
              }
            }}
            size="small"
            sx={{
              '& .MuiToggleButton-root': {
                border: '1px solid',
                borderColor: 'divider',
              }
            }}
          >
            <ToggleButton value="tile" aria-label="Tile view">
              <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
            </ToggleButton>
            <ToggleButton value="table" aria-label="Table view">
              <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
            </ToggleButton>
          </ToggleButtonGroup>

          {/* NEW DATA SET Button - Only show for authorized users */}
          {(canManageDatasets() || isUserSuperAdmin) && (
            <>
              {/* Desktop: Text Button */}
              <Box sx={{ display: { xs: 'none', md: 'block' } }}>
                <Button 
                  variant="contained" 
                  color="secondary" 
                  startIcon={<Add />} 
                  onClick={() => openNewDataSetDialog()}
                  size="small"
                  sx={{ 
                    whiteSpace: 'nowrap'
                  }}
                >
                  New Data Set
                </Button>
              </Box>
            </>
          )}
        </Box>
      </Box>

      {/* Mobile Menu Dialog */}
      <Dialog
        open={mobileMenuOpen}
        onClose={() => setMobileMenuOpen(false)}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          Search & Filters
        </DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            {/* Search Box */}
            <TextField
              label="Search Data Sets"
              variant="outlined"
              size="small"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              fullWidth
            />

            {/* Sort Controls */}
            <FormControl fullWidth size="small">
              <InputLabel>Sort By</InputLabel>
              <Select
                value={sortField}
                label="Sort By"
                onChange={(e) => setSortField(e.target.value as string)}
              >
                <MenuItem value="collectionName">Collection Name</MenuItem>
                <MenuItem value="collectionUpdatedOn">Last Updated On</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth size="small">
              <InputLabel>Order</InputLabel>
              <Select
                value={sortOrder}
                label="Order"
                onChange={(e) => setSortOrder(e.target.value as string)}
              >
                <MenuItem value="asc">ASC</MenuItem>
                <MenuItem value="desc">DSC</MenuItem>
              </Select>
            </FormControl>
            
            <FormControl fullWidth size="small">
              <InputLabel>Status Filter</InputLabel>
              <Select
                value={statusFilter}
                label="Status Filter"
                onChange={(e) => setStatusFilter(e.target.value as string)}
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
                <MenuItem value="deleted">Deleted</MenuItem>
                <MenuItem value="all">All Status</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => setMobileMenuOpen(false)} 
            variant="outlined" 
            color="secondary"
          >
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Mobile Toolbar Actions Dialog */}
      <Dialog
        open={toolbarMenuOpen}
        onClose={() => setToolbarMenuOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Actions</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <Box display="flex" alignItems="center" gap={1}>
              <Typography variant="body2" sx={{ mr: 1 }}>View:</Typography>
              <ToggleButtonGroup
                value={viewMode}
                exclusive
                onChange={(_e, newMode) => {
                  if (newMode !== null) {
                    setViewMode(newMode);
                    localStorage.setItem(VIEWMODE_STORAGE_KEY, newMode);
                  }
                }}
                size="small"
                sx={{
                  '& .MuiToggleButton-root': {
                    border: '1px solid',
                    borderColor: 'divider',
                  }
                }}
              >
                <ToggleButton value="tile" aria-label="Tile view">
                  <Tooltip title="Tile View"><ViewModuleIcon fontSize="small" /></Tooltip>
                </ToggleButton>
                <ToggleButton value="table" aria-label="Table view">
                  <Tooltip title="Table View"><TableRowsIcon fontSize="small" /></Tooltip>
                </ToggleButton>
              </ToggleButtonGroup>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToolbarMenuOpen(false)} variant="outlined" color="secondary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Tile View */}
      {viewMode === 'tile' && (
      <Box
        display="flex"
        flexWrap="wrap"
        gap={2.5}
        sx={{ justifyContent: { xs: 'center', lg: 'flex-start' } }}
      >
        {filteredAndSortedDataSets.map((dataSet) => {
          const datasetName = dataSet.collectionName?.split("-").slice(2).join("-");
          const isReservedDataset = datasetName === "Consent_Records";
          const canEdit = (canManageDatasets() || isUserSuperAdmin) && datasetName !== "Consent_Records";
          const canDelete = (canManageDatasets() || isUserSuperAdmin) &&
                            datasetName !== "Consent_Records";
          const showActionButtons = canEdit || canDelete;

          const rowOffset =
            isMobile && datasetTileSwipeRow?.tileId === dataSet._id
              ? datasetTileSwipeRow!.offset
              : 0;
          const rowDragging =
            isMobile &&
            datasetTileSwipeRow?.tileId === dataSet._id &&
            datasetTileSwipeRow!.dragging;

          if (!isMobile) {
            return (
              <Box
                key={dataSet._id}
                onClick={() => navigate(`/instant-updates/data-sets/${datasetName}`)}
                sx={{
                  width: 260,
                  height: 200,
                  borderRadius: 2.5,
                  overflow: 'hidden',
                  position: 'relative',
                  boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  cursor: 'pointer',
                  flexShrink: 0,
                  border: isReservedDataset ? `2px solid ${theme.palette.secondary.main}` : '1px solid',
                  borderColor: isReservedDataset ? undefined : 'divider',
                  '&:hover .tile-overlay': { height: 120 },
                  '&:hover .tile-details': { opacity: 1 },
                  '&:hover .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                  '@media (hover: none)': {
                    '& .tile-overlay': { height: 120 },
                    '& .tile-details': { opacity: 1 },
                    '& .tile-name-short': { opacity: 0, height: 0, lineHeight: 0 },
                  },
                }}
              >
                <Box sx={{
                  position: 'absolute', top: 0, left: 0, right: 0, bottom: 0,
                  background: isReservedDataset
                    ? reservedDatasetTileGradient
                    : (theme.palette.dashboard?.counterHeroGradient ?? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`),
                  display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0.5,
                }}>
                  <Groups2Icon sx={{ fontSize: 40, color: heroTileFg ?? '#fff', opacity: 0.85 }} />
                  <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: heroTileFg ?? '#fff', lineHeight: 1, textShadow: heroTileTextShadow }}>
                    {typeof dataSet.contactsCount === 'number' ? dataSet.contactsCount.toLocaleString() : '—'}
                  </Typography>
                  <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: heroTileLabelColor, textTransform: 'uppercase', letterSpacing: 1 }}>
                    Contacts
                  </Typography>
                </Box>

                <Box sx={{ position: 'absolute', top: 8, left: 8, right: 8, zIndex: 2, display: 'flex', gap: 0.5 }}>
                  <Chip
                    label={dataSet.accessLevel}
                    color={dataSet.accessLevel === 'private' ? 'success' : 'warning'}
                    size="small"
                    sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.3)', textTransform: 'capitalize' }}
                  />
                  <Chip
                    label={dataSet.collectionStatus}
                    color={
                      dataSet.collectionStatus === 'active' ? 'success' :
                      dataSet.collectionStatus === 'inactive' ? 'warning' :
                      dataSet.collectionStatus === 'deleted' ? 'error' : 'secondary'
                    }
                    size="small"
                    sx={{ height: 22, fontSize: '0.68rem', fontWeight: 700, color: '#fff', boxShadow: '0 1px 4px rgba(0,0,0,0.3)', textTransform: 'capitalize' }}
                  />
                </Box>

                {isReservedDataset && (
                  <Chip
                    label="SYSTEM"
                    size="small"
                    sx={{
                      position: 'absolute', top: 8, right: 8, zIndex: 3, height: 20, fontSize: '0.62rem', fontWeight: 800,
                      color: theme.palette.dashboard?.systemDatasetBadgeTextColor ?? '#fff',
                      backgroundColor: theme.palette.secondary.main,
                      boxShadow: '0 1px 4px rgba(0,0,0,0.3)',
                    }}
                  />
                )}

                <Box
                  className="tile-overlay"
                  sx={{
                    position: 'absolute', bottom: 0, left: 0, right: 0,
                    overflow: 'hidden',
                    background: 'linear-gradient(to top, rgba(0,0,0,0.88) 0%, rgba(0,0,0,0.72) 50%, rgba(0,0,0,0.25) 100%)',
                    transition: 'height 0.35s cubic-bezier(0.4, 0, 0.2, 1)',
                    height: 40, px: 1.5,
                    display: 'flex', flexDirection: 'column', justifyContent: 'flex-start',
                  }}
                >
                  <Typography variant="subtitle2" noWrap className="tile-name-short" sx={{
                    color: '#fff', fontWeight: 700, fontSize: '0.82rem',
                    lineHeight: '40px', textShadow: '0 1px 3px rgba(0,0,0,0.4)', flexShrink: 0,
                    transition: 'opacity 0.2s ease, height 0.2s ease, line-height 0.2s ease',
                  }}>
                    {datasetName}
                  </Typography>

                  <Box className="tile-details" sx={{
                    opacity: 0, transition: 'opacity 0.3s ease 0.08s',
                    display: 'flex', flexDirection: 'column', gap: 0.5, pt: 0.25, pb: 1, flex: 1,
                  }}>
                    <Typography variant="subtitle2" sx={{
                      color: '#fff', fontWeight: 700, fontSize: '0.78rem', lineHeight: 1.3,
                      textShadow: '0 1px 3px rgba(0,0,0,0.4)', wordBreak: 'break-word',
                    }}>
                      {datasetName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                      Owned by: {isReservedDataset ? 'System' : dataSet.collectionOwnedByName || 'Unknown'}
                    </Typography>
                    {dataSet.collectionUpdatedOn && (
                      <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.68rem', lineHeight: 1.2 }}>
                        Updated: {new Date(dataSet.collectionUpdatedOn).toLocaleDateString()}
                      </Typography>
                    )}
                    {showActionButtons && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 'auto' }}>
                        <Box display="flex" gap={0.5}>
                          {canEdit && (
                            <Tooltip title="Edit">
                              <Button color="secondary" variant="contained" size="small"
                                onClick={(e) => { e.stopPropagation(); openStatusDialog(dataSet); }}
                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                                <Edit fontSize="small" />
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                        <Box display="flex" gap={0.5}>
                          {canDelete && (
                            <Tooltip title="Delete">
                              <Button color="error" variant="contained" size="small"
                                onClick={(e) => { e.stopPropagation(); handleDeleteDataSet(datasetName || "", dataSet.collectionStatus); }}
                                sx={{ borderRadius: 1, textTransform: 'none', px: 1.25, minWidth: 36 }}>
                                <Delete fontSize="small" />
                              </Button>
                            </Tooltip>
                          )}
                        </Box>
                      </Box>
                    )}
                  </Box>
                </Box>
              </Box>
            );
          }

          /* Mobile / tablet: iOS-style swipe; gradient shows icon + contacts; overlay = name only */
          return (
            <Box
              key={dataSet._id}
              sx={{
                width: 260,
                height: 200,
                borderRadius: 2.5,
                overflow: 'hidden',
                position: 'relative',
                flexShrink: 0,
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                border: isReservedDataset ? `2px solid ${theme.palette.secondary.main}` : '1px solid',
                borderColor: isReservedDataset ? undefined : 'divider',
                touchAction: 'pan-y',
                isolation: 'isolate',
              }}
            >
              <Box
                sx={{
                  position: 'absolute',
                  inset: 0,
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'stretch',
                  zIndex: 0,
                  borderRadius: 2.5,
                  overflow: 'hidden',
                }}
              >
                {canEdit && (
                  <Box
                    sx={{
                      width: SWIPE_ACTION_WIDTH_PX,
                      flexShrink: 0,
                      bgcolor: 'secondary.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Tooltip title="Edit">
                      <IconButton
                        aria-label="Edit data set"
                        size="medium"
                        sx={{
                          color: 'secondary.contrastText',
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDatasetTileSwipeRow(null);
                          openStatusDialog(dataSet);
                        }}
                      >
                        <Edit fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
                <Box sx={{ flex: 1, minWidth: 0 }} />
                {canDelete && (
                  <Box
                    sx={{
                      width: SWIPE_ACTION_WIDTH_PX,
                      flexShrink: 0,
                      bgcolor: 'error.main',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Tooltip title="Delete">
                      <IconButton
                        aria-label="Delete data set"
                        size="medium"
                        sx={{
                          color: 'error.contrastText',
                          '&:hover': { backgroundColor: 'rgba(0,0,0,0.12)' },
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          setDatasetTileSwipeRow(null);
                          handleDeleteDataSet(datasetName || "", dataSet.collectionStatus);
                        }}
                      >
                        <Delete fontSize="medium" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                )}
              </Box>

              <Box
                onTouchStart={(e) => handleDataSetTileTouchStart(e, dataSet)}
                onTouchMove={(e) => handleDataSetTileTouchMove(e, dataSet, canEdit, canDelete)}
                onTouchEnd={(e) => handleDataSetTileTouchEnd(e, dataSet, canEdit, canDelete, datasetName)}
                onTouchCancel={(e) => handleDataSetTileTouchCancel(e, dataSet, canEdit, canDelete)}
                sx={{
                  position: 'relative',
                  zIndex: 1,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  borderRadius: 2.5,
                  overflow: 'hidden',
                  cursor: 'pointer',
                  backgroundColor: 'background.paper',
                  transform: `translateX(${rowOffset}px)`,
                  transition: rowDragging
                    ? 'none'
                    : 'transform 0.35s cubic-bezier(0.25, 0.8, 0.25, 1)',
                  willChange: rowDragging ? 'transform' : 'auto',
                  boxShadow: rowOffset !== 0 ? 3 : 'none',
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    minHeight: 0,
                    background: isReservedDataset
                      ? reservedDatasetTileGradient
                      : (theme.palette.dashboard?.counterHeroGradient ?? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 60%, ${theme.palette.secondary.main} 100%)`),
                    display: 'flex',
                    flexDirection: 'column',
                    justifyContent: 'space-between',
                  }}
                >
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      gap: 0.5,
                      px: 1,
                    }}
                  >
                    <Groups2Icon sx={{ fontSize: 40, color: heroTileFg ?? '#fff', opacity: 0.85 }} />
                    <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, color: heroTileFg ?? '#fff', lineHeight: 1, textShadow: heroTileTextShadow }}>
                      {typeof dataSet.contactsCount === 'number' ? dataSet.contactsCount.toLocaleString() : '—'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 600, color: heroTileLabelColor, textTransform: 'uppercase', letterSpacing: 1 }}>
                      Contacts
                    </Typography>
                  </Box>
                  <Box
                    sx={{
                      background: 'linear-gradient(to top, rgba(0,0,0,0.92) 0%, rgba(0,0,0,0.75) 55%, rgba(0,0,0,0.2) 100%)',
                      px: 1.5,
                      py: 1.25,
                      minHeight: 48,
                      display: 'flex',
                      alignItems: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Typography
                      variant="subtitle2"
                      sx={{
                        color: '#fff',
                        fontWeight: 700,
                        fontSize: '0.88rem',
                        lineHeight: 1.35,
                        textShadow: '0 1px 4px rgba(0,0,0,0.45)',
                        wordBreak: 'break-word',
                      }}
                    >
                      {datasetName}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
      )}

      {/* Table View */}
      {viewMode === 'table' && (
      <Box sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 1,
        backgroundColor: 'background.paper',
        p: 2,
      }}>
        <DataTableWithStaticSchema
          dataUrl={tableDataUrl}
          schemaObject={dataSetsSchema}
          columnsToExclude={['_id']}
          refreshTrigger={tableRefreshTrigger}
          initialSortColumn="collectionUpdatedOn"
          initialSortOrder="desc"
          dataSetName="datasets-list"
          enableColumnPreferences={true}
          mandatoryColumns={DATASETS_MANDATORY_COLUMNS}
          allowedOptionalColumns={DATASETS_OPTIONAL_COLUMNS}
          columnsWithDisabledFilter={DATASETS_DISABLED_FILTER_COLUMNS}
          customDataProcessor={enrichDataSetData}
          columnRenderers={{
            collectionStatus: (row: any) => {
              const status = row.collectionStatus;
              const colorMap: Record<string, 'success' | 'warning' | 'error' | 'default'> = {
                active: 'success',
                inactive: 'warning',
                deleted: 'error',
              };
              return (
                <Chip
                  label={status}
                  color={colorMap[status] || 'default'}
                  variant="outlined"
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
              );
            },
            accessLevel: (row: any) => {
              const access = row.accessLevel;
              return (
                <Chip
                  label={access}
                  color={access?.toLowerCase() === 'private' ? 'success' : 'warning'}
                  variant="outlined"
                  size="small"
                  sx={{ textTransform: 'capitalize' }}
                />
              );
            },
            contactsCount: (row: any) => {
              return typeof row.contactsCount === 'number' ? String(row.contactsCount) : 'N/A';
            },
          }}
          customActions={(row: any) => {
            const datasetName = row.dataSetName || row.collectionName?.split("-").slice(2).join("-");
            const isReservedDataset = datasetName === "Consent_Records";
            const canEdit = (canManageDatasets() || isUserSuperAdmin) && !isReservedDataset;
            const canDelete = (canManageDatasets() || isUserSuperAdmin) &&
                            !isReservedDataset;
            const actions: { label: string; onClick: (r: any) => void; color: 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'; icon: React.ReactNode }[] = [];

            actions.push({
              label: 'View Users',
              onClick: () => navigate(`/instant-updates/data-sets/${datasetName}`),
              color: 'secondary',
              icon: <VisibilityIcon />,
            });

            if (canEdit) {
              actions.push({
                label: 'Edit',
                onClick: (r: any) => openStatusDialog(r),
                color: 'secondary',
                icon: <Edit />,
              });
            }

            if (canDelete) {
              actions.push({
                label: 'Delete',
                onClick: () => handleDeleteDataSet(datasetName || "", row.collectionStatus),
                color: 'error',
                icon: <Delete />,
              });
            }

            return actions;
          }}
        />
      </Box>
      )}

      <Box>
        {/* Dialog for Add/Edit DataSet (Full Schema) */}
        <Dialog open={dialogOpen} onClose={closeDialog} fullWidth>
          <DialogTitle>{isEditing ? "Edit Collection Schema" : "Add New DataSet"}</DialogTitle>
          <DialogContent>
            <FormControl fullWidth margin="normal">
              <InputLabel>Access Level</InputLabel>
              <Select
                size="small"
                value={currentDataSet?.accessLevel || ""}
                label="Access Level"
                onChange={(e) =>
                  setCurrentDataSet((prev: any) =>
                    prev ? { ...prev, accessLevel: e.target.value } : null
                  )
                }
              >
                <MenuItem value="Public">Public</MenuItem>
                <MenuItem value="Private">Private</MenuItem>
              </Select>
            </FormControl>
            {Object.keys(currentDataSet || {}).filter((key) => isEditing ? key !== "_id" && key !== "companyId" && key !== "__v"
              && key !== "collectionOwnedByUserId" && key !== "collectionUpdatedByUserId" && key !== "collectionUpdatedOn"
              && key !== "sharedTo" && key !== "createdAt" && key !== "updatedAt" && key !== "collectionSchema"
              :
              key !== "_id" && key !== "companyId" && key !== "__v"
              && key !== "collectionOwnedByUserId" && key !== "collectionUpdatedByUserId" && key !== "collectionUpdatedOn"
              && key !== "sharedTo" && key !== "accessLevel" && key !== "collectionStatus" && key !== "createdAt" && key !== "updatedAt").map((key) =>
                key !== "collectionSchema" ? (
                  <TextField
                    size="small"
                    key={key}
                    label={CamelCaseToName(key)}
                    value={(currentDataSet || {})[key] || ""}
                    onChange={(e) => {
                      const value = e.target.value;
                      const regex = new RegExp("^[a-zA-Z0-9_\\-]*$"); // FIXED: used RegExp constructor
                      if (key === 'collectionName' && !regex.test(value)) {
                        setCollectionNameError('Only alphabets, numbers, underscore, and hyphen are allowed.');
                      } else {
                        setCollectionNameError('');
                      }
                      setCurrentDataSet((prev: any) =>
                        prev ? { ...prev, [key]: value } : null
                      );
                    }}
                    fullWidth
                    margin="normal"
                    disabled={key === "keyName"}
                    required={key === "keyName"}
                    error={key === 'collectionName' && !!collectionNameError}
                    helperText={key === 'collectionName' && collectionNameError}
                  />
                ) : (
                  <Box key={key}>
                    <p>Schema</p>
                    {(currentDataSet?.collectionSchema || []).map((field: any, index: number) => (
                      <Box key={index} display="flex" gap={2} alignItems="flex-start" mb={2}>
                        <TextField
                          size="small"
                          label={CamelCaseToName("fieldName") + " *"}
                          value={field.keyName}
                          onChange={(e) =>
                            handleFieldChange(index, "keyName", e.target.value)
                          }
                          error={!!field.error || !field.keyName}
                          helperText={field.error || (!field.keyName ? "Field name is required" : "")}
                          required
                          sx={{ minWidth: 200, height: 56 }}
                        />
                        <FormControl sx={{ minWidth: 150, height: 56 }} size="small" required error={!field.type}>
                          <InputLabel>Type *</InputLabel>
                          <Select
                            value={field.type}
                            label="Type *"
                            onChange={(e) =>
                              handleFieldChange(index, "type", e.target.value)
                            }
                            error={!field.type}
                          >
                            <MenuItem value="String">String</MenuItem>
                            <MenuItem value="Number">Number</MenuItem>
                            <MenuItem value="Boolean">Boolean</MenuItem>
                          </Select>
                          {!field.type && (
                            <FormHelperText error>Type is required</FormHelperText>
                          )}
                        </FormControl>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', minHeight: 56 }}>
                          <FormControlLabel
                            control={
                              <Switch
                                checked={field.required}
                                onChange={(e) =>
                                  handleFieldChange(index, "required", e.target.checked)
                                }
                                name="required"
                                color="secondary"
                                sx={{
                                  '& .MuiSwitch-switchBase.Mui-checked': {
                                    color: (theme) => theme.palette.secondary.main,
                                    '&:hover': {
                                      backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 227, 163, 0.08)' 
                                        : 'rgba(204, 167, 82, 0.08)',
                                    },
                                  },
                                  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                                    backgroundColor: (theme) => theme.palette.secondary.main,
                                  },
                                  '& .MuiSwitch-switchBase:not(.Mui-checked)': {
                                    color: (theme) => theme.palette.mode === 'dark' 
                                      ? theme.palette.grey[400] 
                                      : theme.palette.grey[600],
                                    '&:hover': {
                                      backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                        ? 'rgba(255, 255, 255, 0.08)' 
                                        : 'rgba(0, 0, 0, 0.08)',
                                    },
                                  },
                                  '& .MuiSwitch-track': {
                                    backgroundColor: (theme) => theme.palette.mode === 'dark' 
                                      ? theme.palette.grey[600] 
                                      : theme.palette.grey[400],
                                  },
                                }}
                              />
                            }
                            label="Required"
                            sx={{ margin: 0 }}
                          />
                        </Box>
                        <Box sx={{ display: 'flex', alignItems: 'flex-start', minHeight: 56 }}>
                          <IconButton 
                            onClick={() => handleRemoveFieldSet(index)}
                            sx={{ 
                              border: '1px solid',
                              borderColor: 'error.main',
                              color: 'error.main',
                              '&:hover': {
                                backgroundColor: 'error.main',
                                color: 'white'
                              }
                            }}
                          >
                            <Remove />
                          </IconButton>
                        </Box>
                      </Box>
                    ))}
                    <Button
                      color="secondary"
                      startIcon={<Add />}
                      onClick={handleAddFieldSet}
                      variant="outlined"
                    >
                      ADD FIELD SET
                    </Button>
                  </Box>
                )
              )}
          </DialogContent>
          <DialogActions>
            <Button
              variant="outlined" color="secondary"
              onClick={closeDialog}
              startIcon={<Cancel />}
            >
              Cancel
            </Button>
            <Button
              onClick={saveDataSet}
              startIcon={<Save />}
              variant="contained" color="success"
              disabled={
                !!collectionNameError || 
                (currentDataSet?.collectionSchema.some((field: any) => field.error)) ||
                (currentDataSet?.collectionSchema.some((field: any) => !field.keyName || !field.type))
              }
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Dialog for Editing DataSet Status and Access Level */}
        <Dialog open={statusDialogOpen} onClose={closeStatusDialog} fullWidth maxWidth="sm">
          <DialogTitle>Edit Collection Settings</DialogTitle>
          <DialogContent>
            <Typography variant="h6" sx={{ mb: 2 }}>
              Collection: {currentStatusDataSet?.collectionName?.split("-").slice(2).join("-")}
            </Typography>
            <FormControl fullWidth margin="normal">
              <InputLabel>Access Level</InputLabel>
              <Select
                size="small"
                value={currentStatusDataSet?.accessLevel || ""}
                label="Access Level"
                onChange={(e) =>
                  setCurrentStatusDataSet((prev: any) =>
                    prev ? { ...prev, accessLevel: e.target.value } : null
                  )
                }
              >
                <MenuItem value="public">Public</MenuItem>
                <MenuItem value="private">Private</MenuItem>
              </Select>
            </FormControl>
            <FormControl fullWidth margin="normal" sx={{ mt: 2 }}>
              <InputLabel>Status</InputLabel>
              <Select
                size="small"
                value={currentStatusDataSet?.collectionStatus || ""}
                label="Status"
                onChange={(e) =>
                  setCurrentStatusDataSet((prev: any) =>
                    prev ? { ...prev, collectionStatus: e.target.value } : null
                  )
                }
              >
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </DialogContent>
          <DialogActions>
            <Button variant="outlined" color="secondary" onClick={closeStatusDialog} startIcon={<Cancel />}>
              Cancel
            </Button>
            <Button onClick={handleSaveStatus} startIcon={<Save />} variant="contained" color="success">
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {/* Snackbar for notifications */}
        <Snackbar
          open={snackbar.open}
          autoHideDuration={6000}
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        >
          <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity} sx={{ width: '100%' }}>
            {snackbar.message}
          </Alert>
        </Snackbar>
      </Box>
    </Box>
  );
};